/**
 * Profile Management Zod Validation Schemas
 *
 * Schemas for wallet addresses, ENS/SNS names, social links, and avatar uploads
 * Mirrors backend validation patterns from:
 * - cdk-anyw3ar-backend/functions/webapp/owner/profile/schemas/WalletSchemas.ts
 * - cdk-anyw3ar-backend/functions/webapp/owner/profile/schemas/SocialLinkSchemas.ts
 */

import { z } from 'zod';

/**
 * ====================================
 * WALLET VALIDATION SCHEMAS
 * ====================================
 */

/**
 * Ethereum Address Validation
 * Format: 0x + 40 hexadecimal characters
 * Total length: 42 characters
 */
const ethAddressRegex = /^0x[a-fA-F0-9]{40}$/;

/**
 * Solana Address Validation
 * Format: Base58 encoded, 32-44 characters (typically 44)
 */
const solAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

/**
 * Bitcoin Address Validation
 * Supports: P2PKH (1...), P2SH (3...), Bech32 (bc1...)
 * Length: 26-62 characters
 */
const btcAddressRegex = /^(1|3|bc1)[a-zA-HJ-NP-Z0-9]{25,62}$/;

/**
 * ENS Name Schema (Ethereum Name Service)
 * Format: lowercase alphanumeric + hyphens, must end with .eth
 * Example: vitalik.eth, user-123.eth
 */
export const ensNameSchema = z.string().regex(
  /^[a-z0-9-]+\.eth$/,
  'ENS name must end with .eth and contain only lowercase letters, numbers, and hyphens'
);

/**
 * SNS Name Schema (Solana Name Service)
 * Format: lowercase alphanumeric + hyphens, must end with .sol
 * Example: solana.sol, user-123.sol
 */
export const snsNameSchema = z.string().regex(
  /^[a-z0-9-]+\.sol$/,
  'SNS name must end with .sol and contain only lowercase letters, numbers, and hyphens'
);

/**
 * Wallet Schemas - Type-specific validation
 */
export const walletSchemas = {
  ETH: z.object({
    type: z.literal('ETH'),
    address: z
      .string()
      .min(1, 'Ethereum address is required')
      .regex(ethAddressRegex, 'Invalid Ethereum address format'),
    ensName: z
      .union([
        ensNameSchema,
        z.literal(''), // Empty string allowed (clearing name)
        z.null(),      // Null allowed
      ])
      .optional(),
    visible: z.boolean().default(true),
  }),

  SOL: z.object({
    type: z.literal('SOL'),
    address: z
      .string()
      .min(1, 'Solana address is required')
      .regex(solAddressRegex, 'Invalid Solana address format'),
    ensName: z
      .union([
        snsNameSchema,
        z.literal(''), // Empty string allowed (clearing name)
        z.null(),      // Null allowed
      ])
      .optional(),
    visible: z.boolean().default(true),
  }),

  BTC: z.object({
    type: z.literal('BTC'),
    address: z
      .string()
      .min(1, 'Bitcoin address is required')
      .regex(btcAddressRegex, 'Invalid Bitcoin address format'),
    ensName: z
      .union([
        z.literal(''), // Empty string allowed
        z.null(),      // Null allowed
      ])
      .optional()
      .refine(
        (val) => !val || val === '' || val === null,
        { message: 'Bitcoin wallets cannot have ENS names' }
      ),
    visible: z.boolean().default(true),
  }),
};

export type WalletFormData = z.infer<typeof walletSchemas.ETH> | z.infer<typeof walletSchemas.SOL> | z.infer<typeof walletSchemas.BTC>;

/**
 * Export inferred types for ENS/SNS names
 */
export type EnsName = z.infer<typeof ensNameSchema>;
export type SnsName = z.infer<typeof snsNameSchema>;

/**
 * ====================================
 * SOCIAL LINK VALIDATION SCHEMAS
 * ====================================
 */

/**
 * Supported Social Media Platforms
 * Aligns with backend SocialLinkSchemas.ts
 * Story 3.4.a.2: Added email platform
 * Story 3.4.d: Replaced spotify with myplaylist (multi-platform music links)
 * Story 3.4.e: Added phone platform
 */
export const socialLinkPlatforms = [
  'twitter',
  'instagram',
  'facebook',
  'linkedin',
  'youtube',
  'tiktok',
  'discord',
  'telegram',
  'github',
  'email',
  'phone',
  'myplaylist',
] as const;

export type SocialLinkPlatform = (typeof socialLinkPlatforms)[number];

/**
 * MyPlaylist Multi-Platform URL Validation Patterns (Story 3.4.d)
 * Accepts URLs from 5 different music streaming platforms
 * Mirrors backend validation from SocialLinkSchemas.ts
 */
const myPlaylistPatterns = {
  // Deezer: https://deezer.com/user/123456 or https://www.deezer.com/en/user/123456
  deezer: /^https:\/\/(www\.)?deezer\.com\/(en\/)?user\/[\w-]+$/,

  // Spotify: https://open.spotify.com/user/username or https://open.spotify.com/artist/artistid
  spotify: /^https:\/\/open\.spotify\.com\/(user|artist)\/[\w-]+$/,

  // Apple Music: https://music.apple.com/us/artist/name/id123 or https://music.apple.com/fr/profile/username
  appleMusic: /^https:\/\/music\.apple\.com\/[a-z]{2}\/(artist|profile)\/[\w-]+\/[\w-]+$/,

  // YouTube Music: https://music.youtube.com/channel/UC... or https://music.youtube.com/@handle
  youtubeMusic: /^https:\/\/music\.youtube\.com\/(channel\/UC[\w-]+|@[\w-]+)$/,

  // Tidal: https://tidal.com/browse/artist/12345 or https://tidal.com/user/username or https://www.tidal.com/artist/12345
  tidal: /^https:\/\/(www\.)?tidal\.com\/(browse\/)?(artist|user)\/[\w-]+$/,
};

/**
 * Helper function to validate MyPlaylist URLs against all 5 platform patterns
 * Returns true if URL matches ANY of the supported music streaming platforms
 * Story 3.4.d
 */
function isValidMyPlaylistUrl(url: string): boolean {
  if (url === '') return true; // Empty URL is valid (indicates clearing the field)
  return Object.values(myPlaylistPatterns).some((pattern) => pattern.test(url));
}

/**
 * Detect which music streaming platform the user is attempting to use
 * Returns platform identifier or null if domain is not recognized
 * Story 3.4.d: Platform-specific error messages
 */
function detectMyPlaylistPlatform(url: string): 'deezer' | 'spotify' | 'appleMusic' | 'youtubeMusic' | 'tidal' | null {
  if (url.includes('deezer.com')) return 'deezer';
  if (url.includes('spotify.com')) return 'spotify';
  if (url.includes('music.apple.com')) return 'appleMusic';
  if (url.includes('music.youtube.com')) return 'youtubeMusic';
  if (url.includes('tidal.com')) return 'tidal';
  return null;
}

/**
 * Generate platform-specific error message for invalid MyPlaylist URLs
 * Detects the platform from the URL and returns helpful guidance
 * Story 3.4.d: Improved UX with platform-specific error messages
 */
function getMyPlaylistErrorMessage(url: string): string {
  const platform = detectMyPlaylistPlatform(url);

  const errorMessages = {
    deezer: 'Invalid Deezer URL. Use: https://deezer.com/user/username or https://www.deezer.com/en/user/username',
    spotify: 'Invalid Spotify URL. Use: https://open.spotify.com/user/username or https://open.spotify.com/artist/artistid',
    appleMusic: 'Invalid Apple Music URL. Use: https://music.apple.com/us/artist/name/id or https://music.apple.com/us/profile/username/id',
    youtubeMusic: 'Invalid YouTube Music URL. Use: https://music.youtube.com/@username or https://music.youtube.com/channel/UCxxxxx',
    tidal: 'Invalid Tidal URL. Use: https://tidal.com/artist/artistid or https://tidal.com/user/username',
  };

  return platform
    ? errorMessages[platform]
    : 'URL must be a valid music streaming profile URL (Deezer, Spotify, Apple Music, YouTube Music, or Tidal)';
}

/**
 * Platform-Specific URL Validation Patterns
 * Mirrors backend patterns exactly from SocialLinkSchemas.ts
 * Story 3.4.a.2: Added email pattern
 * Story 3.4.d: Replaced spotify with myplaylist (multi-platform)
 * Story 3.4.e: Added phone pattern
 */
const platformUrlPatterns: Record<SocialLinkPlatform, RegExp> = {
  twitter: /^https:\/\/(www\.)?(twitter\.com|x\.com)\/.+$/,
  instagram: /^https:\/\/(www\.)?instagram\.com\/.+$/,
  facebook: /^https:\/\/(www\.)?facebook\.com\/.+$/,
  linkedin: /^https:\/\/(www\.)?linkedin\.com\/(in|company)\/.+$/,
  youtube: /^https:\/\/(www\.)?youtube\.com\/(c|channel|user|@).+$/,
  tiktok: /^https:\/\/(www\.)?tiktok\.com\/@.+$/,
  discord: /^https:\/\/(www\.)?(discord\.gg|discord\.com\/(invite|servers))\/.+$/,
  telegram: /^https:\/\/(t\.me|telegram\.me)\/.+$/,
  github: /^https:\/\/(www\.)?github\.com\/.+$/,
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  phone: /^[+\d\s()-]{8,20}$/,
  // Story 3.4.d: MyPlaylist - multi-platform pattern (validated via isValidMyPlaylistUrl)
  // This is a placeholder pattern; actual validation uses OR logic across 5 platforms
  myplaylist: /^https:\/\//,
};

/**
 * Social URL Schemas - Platform-specific validation
 * Each schema validates:
 * 1. HTTPS protocol (HTTP not allowed) - EXCEPT for email and phone platforms
 * 2. Platform-specific domain pattern
 * 3. Max URL length (2048 characters)
 */
export const socialUrlSchemas: Record<SocialLinkPlatform, z.ZodString | z.ZodEffects<z.ZodString, string, string>> = {
  twitter: z.string().regex(
    platformUrlPatterns.twitter,
    'Must be a valid Twitter/X profile URL (https://twitter.com/username or https://x.com/username)'
  ),
  instagram: z.string().regex(
    platformUrlPatterns.instagram,
    'Must be a valid Instagram URL (https://instagram.com/username)'
  ),
  facebook: z.string().regex(
    platformUrlPatterns.facebook,
    'Must be a valid Facebook URL (https://facebook.com/username)'
  ),
  linkedin: z.string().regex(
    platformUrlPatterns.linkedin,
    'Must be a valid LinkedIn URL (https://linkedin.com/in/username or https://linkedin.com/company/name)'
  ),
  youtube: z.string().regex(
    platformUrlPatterns.youtube,
    'Must be a valid YouTube URL (https://youtube.com/@channel or https://youtube.com/c/channel)'
  ),
  tiktok: z.string().regex(
    platformUrlPatterns.tiktok,
    'Must be a valid TikTok URL (https://tiktok.com/@username)'
  ),
  discord: z.string().regex(
    platformUrlPatterns.discord,
    'Must be a valid Discord URL (https://discord.gg/invite or https://discord.com/invite/code)'
  ),
  telegram: z.string().regex(
    platformUrlPatterns.telegram,
    'Must be a valid Telegram URL (https://t.me/username or https://telegram.me/username)'
  ),
  github: z.string().regex(
    platformUrlPatterns.github,
    'Must be a valid GitHub URL (https://github.com/username)'
  ),
  email: z.string().regex(
    platformUrlPatterns.email,
    'Must be a valid email address (user@example.com)'
  ),
  phone: z.string().regex(
    platformUrlPatterns.phone,
    'Invalid phone number format. Use international format (+33 6 12 34 56 78)'
  ),
  // Story 3.4.d: MyPlaylist - multi-platform validation with platform-specific error messages
  myplaylist: z.string().refine(
    isValidMyPlaylistUrl,
    (url) => ({ message: getMyPlaylistErrorMessage(url) })
  ),
};

/**
 * Helper function to validate social URL for a specific platform
 *
 * @param platform - Social media platform name
 * @param url - URL to validate
 * @returns Validation result with success flag and optional error message
 *
 * @example
 * ```typescript
 * validateSocialUrl('twitter', 'https://twitter.com/anyw3ar')
 * // => { success: true }
 *
 * validateSocialUrl('twitter', 'http://twitter.com/anyw3ar')
 * // => { success: false, error: 'Must be a valid Twitter/X profile URL...' }
 * ```
 */
export function validateSocialUrl(
  platform: string,
  url: string
): { success: boolean; error?: string } {
  // Check if platform is supported
  if (!socialLinkPlatforms.includes(platform as SocialLinkPlatform)) {
    return {
      success: false,
      error: `Unsupported platform: ${platform}`,
    };
  }

  // Get schema for platform
  const schema = socialUrlSchemas[platform as SocialLinkPlatform];
  const result = schema.safeParse(url);

  if (result.success) {
    return { success: true };
  }

  return {
    success: false,
    error: result.error.issues[0]?.message || 'Invalid URL format',
  };
}

/**
 * Create dynamic social link schema for use in forms
 * Validates platform-specific URL patterns
 *
 * @param platform - Social media platform name
 * @returns Zod schema for the platform's URL validation
 */
export const createSocialLinkSchema = (platform: string) => {
  const schema = socialUrlSchemas[platform as keyof typeof socialUrlSchemas];
  if (!schema) {
    // Default validation for unknown platforms
    return z
      .string()
      .min(1, `${platform} URL is required`)
      .url('Must be a valid URL')
      .refine((url) => url.startsWith('https://'), 'Must use HTTPS');
  }
  return schema;
};

/**
 * Platform URL Examples - Useful for UI placeholders and error messages
 * Story 3.4.a.2: Added email example
 * Story 3.4.d: Replaced spotify with myplaylist (multi-platform music links)
 * Story 3.4.e: Added phone example
 */
export const platformUrlExamples: Record<SocialLinkPlatform, string> = {
  twitter: 'https://twitter.com/username or https://x.com/username',
  instagram: 'https://instagram.com/username',
  facebook: 'https://facebook.com/username',
  linkedin: 'https://linkedin.com/in/username or https://linkedin.com/company/name',
  youtube: 'https://youtube.com/@channel or https://youtube.com/c/channel',
  tiktok: 'https://tiktok.com/@username',
  discord: 'https://discord.gg/invite or https://discord.com/invite/code',
  telegram: 'https://t.me/username or https://telegram.me/username',
  github: 'https://github.com/username',
  email: 'user@example.com',
  phone: '+33 6 12 34 56 78',
  // Story 3.4.d: MyPlaylist supports 5 music platforms
  myplaylist: 'Deezer, Spotify, Apple Music, YouTube Music, or Tidal profile URL',
};

/**
 * ====================================
 * AVATAR UPLOAD VALIDATION SCHEMA
 * ====================================
 */

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MIN_DIMENSION = 200;
const MAX_DIMENSION = 2000;

export const avatarUploadSchema = z.object({
  file: z
    .instanceof(File)
    .refine((file) => file.size <= MAX_FILE_SIZE, 'File size must be less than 5MB')
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file.type),
      'File must be JPEG, PNG, or WebP'
    ),
});

export type AvatarUploadFormData = z.infer<typeof avatarUploadSchema>;

/**
 * Image dimension validation (must be done separately with Image API)
 */
export const validateImageDimensions = (file: File): Promise<{ valid: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
        resolve({
          valid: false,
          error: `Image must be at least ${MIN_DIMENSION}x${MIN_DIMENSION} pixels`,
        });
      } else if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION) {
        resolve({
          valid: false,
          error: `Image must not exceed ${MAX_DIMENSION}x${MAX_DIMENSION} pixels`,
        });
      } else {
        resolve({ valid: true });
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ valid: false, error: 'Invalid image file' });
    };

    img.src = objectUrl;
  });
};
